import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createQueryHooks } from '../lib/create-query-hooks.ts';
import * as endpoints from '../api/endpoints/projects.ts';
import type {
  UpdateProjectParams,
  UpdateDeployConfigParams,
  HandoffProjectConfig,
} from '../api/types.ts';
import { STALE_TIMES, GC_TIMES } from '../lib/constants.ts';

const {
  queryKeys: _projectQueryKeys,
  useList: useProjects,
  useDetail: useProject,
  useCreate: useCreateProject,
  useDelete: useDeleteProject,
} = createQueryHooks({
  resourceKey: 'projects',
  endpoints: {
    list: endpoints.listProjects,
    getById: endpoints.getProject,
    create: endpoints.createProject,
    delete: endpoints.deleteProject,
  },
});

// Extended query keys (superset of factory's queryKeys)
export const projectQueryKeys = {
  ..._projectQueryKeys,
  servers: (projectId: string) => ['projects', 'servers', projectId] as const,
  deployConfig: (projectId: string) => ['projects', 'deploy-config', projectId] as const,
  handoffConfig: (projectId: string) => ['projects', 'handoff-config', projectId] as const,
  handoffJson: (projectId: string) => ['projects', 'handoff-json', projectId] as const,
};

// useUpdate kept manual — consumers call with { projectId, params }, not { id, params }
export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, params }: { projectId: string; params: UpdateProjectParams }) =>
      endpoints.updateProject(projectId, params),
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectQueryKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectQueryKeys.all });
    },
  });
}

// --- Server association ---

export function useProjectServers(projectId: string) {
  return useQuery({
    queryKey: projectQueryKeys.servers(projectId),
    queryFn: () => endpoints.listProjectServers(projectId),
    staleTime: STALE_TIMES.servers,
    gcTime: GC_TIMES.servers,
    enabled: !!projectId,
  });
}

export function useAssociateServer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, serverId }: { projectId: string; serverId: string }) =>
      endpoints.associateServer(projectId, serverId),
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectQueryKeys.servers(projectId) });
    },
  });
}

export function useDisassociateServer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, serverId }: { projectId: string; serverId: string }) =>
      endpoints.disassociateServer(projectId, serverId),
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectQueryKeys.servers(projectId) });
    },
  });
}

// --- Deploy config ---

export function useDeployConfig(projectId: string) {
  return useQuery({
    queryKey: projectQueryKeys.deployConfig(projectId),
    queryFn: () => endpoints.getDeployConfig(projectId),
    staleTime: STALE_TIMES.projects,
    gcTime: GC_TIMES.projects,
    enabled: !!projectId,
  });
}

export function useUpdateDeployConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      config,
    }: {
      projectId: string;
      config: UpdateDeployConfigParams;
    }) => endpoints.updateDeployConfig(projectId, config),
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectQueryKeys.deployConfig(projectId) });
      queryClient.invalidateQueries({ queryKey: projectQueryKeys.detail(projectId) });
    },
  });
}

function invalidateHandoffQueries(queryClient: ReturnType<typeof useQueryClient>, projectId: string) {
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.handoffConfig(projectId) });
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.handoffJson(projectId) });
  queryClient.invalidateQueries({ queryKey: ['project-handoff', projectId] });
}

export function useHandoffConfig(projectId: string) {
  return useQuery({
    queryKey: projectQueryKeys.handoffConfig(projectId),
    queryFn: () => endpoints.getHandoffConfig(projectId),
    staleTime: STALE_TIMES.projects,
    gcTime: GC_TIMES.projects,
    enabled: !!projectId,
  });
}

export function useUpdateHandoffConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      config,
    }: {
      projectId: string;
      config: HandoffProjectConfig;
    }) => endpoints.updateHandoffConfig(projectId, config),
    onSuccess: (_data, { projectId }) => {
      invalidateHandoffQueries(queryClient, projectId);
      queryClient.invalidateQueries({ queryKey: projectQueryKeys.detail(projectId) });
    },
  });
}

export function useSyncHandoffFromRepo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      ref,
    }: {
      projectId: string;
      ref?: string;
    }) => endpoints.syncHandoffFromRepo(projectId, ref),
    onSuccess: (_data, { projectId }) => {
      invalidateHandoffQueries(queryClient, projectId);
      queryClient.invalidateQueries({ queryKey: projectQueryKeys.detail(projectId) });
    },
  });
}

export function usePushHandoffToRepo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      ref,
      path,
      message,
      handoff,
    }: {
      projectId: string;
      ref?: string;
      path?: string;
      message?: string;
      handoff?: HandoffProjectConfig;
    }) => endpoints.pushHandoffToRepo(projectId, { ref, path, message, handoff }),
    onSuccess: (_data, { projectId }) => {
      invalidateHandoffQueries(queryClient, projectId);
      queryClient.invalidateQueries({ queryKey: projectQueryKeys.detail(projectId) });
    },
  });
}

export { useProjects, useProject, useCreateProject, useDeleteProject };
